/**
 * EmployeeDashboard.js
 *
 * CHANGES:
 * 1. BirthdayAnniversaryWidget — now shown to ALL roles (not just HR/Admin).
 *    Every employee in the company can see upcoming birthdays & anniversaries
 *    so they can send wishes to colleagues.
 *    Backend: /hr/upcoming-events now open to all authenticated users (hr.routes.js updated).
 *
 * 2. Upcoming Holidays — fetched live from GET /hr/holidays instead of hardcoded.
 *    Shows only future/today holidays sorted by date (no more past dates).
 *    Displays up to 5 next holidays.
 *
 * All other sections unchanged.
 */
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { Calendar, FileText, Gift, ArrowRight, Cake, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';
import AttendanceHistory from './AttendanceHistory';
import AnnouncementTimeline from '../../components/announcements/AnnouncementTimeline';

// ── Helper: ordinal suffix ────────────────────────────────────────────────────
const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// ── Helper: initials avatar ───────────────────────────────────────────────────
const InitialsAvatar = ({ firstName = '', lastName = '', size = 36, color = '#4f46e5' }) => (
    <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: `${color}18`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: size * 0.38,
    }}>
        {(firstName[0] || '').toUpperCase()}{(lastName[0] || '').toUpperCase()}
    </div>
);

// ── Helper: format holiday date for display ───────────────────────────────────
const fmtHolidayDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(String(dateStr).slice(0, 10) + 'T00:00:00')
        .toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

// ── Helper: is date today or future ──────────────────────────────────────────
const isTodayOrFuture = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
    return d >= today;
};

// ── Helper: is date today ─────────────────────────────────────────────────────
const isToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date().toISOString().slice(0, 10);
    return String(dateStr).slice(0, 10) === today;
};

// ═══════════════════════════════════════════════════════════════════════════
// BirthdayAnniversaryWidget — visible to ALL employees
// ═══════════════════════════════════════════════════════════════════════════
const BirthdayAnniversaryWidget = () => {
    const [events,  setEvents]  = useState({ birthdays: [], anniversaries: [] });
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(false);

    useEffect(() => {
        axiosInstance.get('/hr/upcoming-events')
            .then(res => setEvents(res.data.data || { birthdays: [], anniversaries: [] }))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    // Merge and sort by days_until (0 = today first)
    const allEvents = [
        ...(events.birthdays || []).map(b => ({
            ...b,
            type:     'birthday',
            emoji:    '🎂',
            subtitle: b.days_until === 0
                ? `Turns ${b.age_turning} today!`
                : `Turns ${b.age_turning} in ${b.days_until} day${b.days_until !== 1 ? 's' : ''}`,
            color:    '#e11d48',
        })),
        ...(events.anniversaries || []).map(a => ({
            ...a,
            type:     'anniversary',
            emoji:    '🌟',
            subtitle: a.days_until === 0
                ? `${ordinal(a.years_completing)} work anniversary today!`
                : `${ordinal(a.years_completing)} work anniversary in ${a.days_until} day${a.days_until !== 1 ? 's' : ''}`,
            color:    '#7c3aed',
        })),
    ].sort((a, b) => a.days_until - b.days_until).slice(0, 6);

    const todayCount = allEvents.filter(e => e.days_until === 0).length;

    if (loading) return (
        <Card className="border-0 shadow-sm mb-4 p-4">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <Cake size={18} className="text-danger"/> Birthdays &amp; Anniversaries
            </h6>
            <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="primary"/>
            </div>
        </Card>
    );

    if (error) return null; // silent fail — don't break dashboard

    return (
        <Card className="border-0 shadow-sm mb-4 p-4 bg-white">
            <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                <Cake size={18} className="text-danger"/>
                Birthdays &amp; Anniversaries
                {todayCount > 0 && (
                    <Badge bg="danger" pill style={{ fontSize: 10 }}>
                        {todayCount} Today!
                    </Badge>
                )}
            </h6>
            <p className="text-muted small mb-3">Next 30 days</p>

            {allEvents.length === 0 ? (
                <div className="text-center py-3 text-muted small">
                    <Star size={28} className="d-block mx-auto mb-2 opacity-30"/>
                    No upcoming birthdays or anniversaries in the next 30 days.
                </div>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {allEvents.map((ev, i) => (
                        <div key={`${ev.type}-${ev.id}-${i}`}
                            className="d-flex align-items-center gap-3 p-2 rounded"
                            style={{
                                background:  ev.days_until === 0 ? `${ev.color}10` : '#f8fafc',
                                border:      ev.days_until === 0 ? `1px solid ${ev.color}30` : '1px solid transparent',
                                transition:  'background 0.15s',
                            }}>
                            <InitialsAvatar
                                firstName={ev.first_name}
                                lastName={ev.last_name}
                                size={36}
                                color={ev.color}/>
                            <div className="flex-grow-1 min-w-0">
                                <div className="fw-bold small text-truncate">
                                    {ev.first_name} {ev.last_name}
                                    <span className="ms-1">{ev.emoji}</span>
                                </div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                    {ev.subtitle}
                                    {ev.department_name && (
                                        <span className="ms-1 text-muted">· {ev.department_name}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex-shrink-0 text-end">
                                {ev.days_until === 0 ? (
                                    <span style={{
                                        fontSize: 10, fontWeight: 700,
                                        color: ev.color, background: `${ev.color}15`,
                                        padding: '2px 8px', borderRadius: 20,
                                    }}>
                                        Today 🎉
                                    </span>
                                ) : (
                                    <span className="text-muted" style={{ fontSize: 11 }}>
                                        {ev.days_until === 1 ? 'Tomorrow' : `${ev.days_until}d`}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// UpcomingHolidaysWidget — fetches live from API, shows only future holidays
// ═══════════════════════════════════════════════════════════════════════════
const UpcomingHolidaysWidget = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        // Fetch current year first, then next year if needed to fill 5 slots
        axiosInstance.get('/hr/holidays', { params: { year: currentYear } })
            .then(res => {
                const all = res.data.data || [];
                // Keep only today and future, sort ascending
                const upcoming = all
                    .filter(h => isTodayOrFuture(h.holiday_date))
                    .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date))
                    .slice(0, 5);

                // If fewer than 3 results, also try next year
                if (upcoming.length < 3) {
                    return axiosInstance
                        .get('/hr/holidays', { params: { year: currentYear + 1 } })
                        .then(r2 => {
                            const next = (r2.data.data || [])
                                .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
                            const combined = [...upcoming, ...next].slice(0, 5);
                            setHolidays(combined);
                        })
                        .catch(() => setHolidays(upcoming));
                }
                setHolidays(upcoming);
            })
            .catch(() => setHolidays([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Card className="border-0 shadow-sm mb-4 p-4 bg-white custom-card">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <Gift size={18} className="text-danger"/> Upcoming Holidays
            </h6>

            {loading ? (
                <div className="text-center py-2">
                    <Spinner animation="border" size="sm" variant="primary"/>
                </div>
            ) : holidays.length === 0 ? (
                <p className="text-muted small mb-0">No upcoming holidays found.</p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {holidays.map((h, i) => (
                        <div key={h.id || i}
                            className="small border-start ps-3 py-2"
                            style={{
                                borderColor: isToday(h.holiday_date) ? '#4f46e5' : '#e5e7eb',
                                borderWidth:  2,
                                borderStyle: 'solid',
                                borderTop:   'none',
                                borderRight: 'none',
                                borderBottom:'none',
                                background:  isToday(h.holiday_date) ? '#f0f4ff' : 'transparent',
                                borderRadius: '0 6px 6px 0',
                            }}>
                            <div className="fw-bold">
                                {h.holiday_name}
                                {isToday(h.holiday_date) && (
                                    <Badge bg="primary" pill className="ms-2" style={{ fontSize: 9 }}>
                                        Today
                                    </Badge>
                                )}
                            </div>
                            <div className="text-muted">{fmtHolidayDate(h.holiday_date)}</div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// EmployeeDashboard
// ═══════════════════════════════════════════════════════════════════════════
const EmployeeDashboard = ({ user, history, loadingHistory, refreshHistory, hideCheckIn }) => {
    return (
        <div className="animate-fade-in p-4">
            <div className="mb-4">
                <h3 className="fw-bold">Good Day, {user?.first_name || 'Team Member'}!</h3>
                <p className="text-muted">
                    You are logged into {user?.company?.name || 'PeoplesDock'}.
                </p>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Row className="g-3">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm p-4 h-100 bg-white custom-card">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3 mb-3 d-inline-block">
                                    <FileText className="text-primary" size={24}/>
                                </div>
                                <h6 className="fw-bold">Payroll &amp; Tax</h6>
                                <p className="small text-muted">
                                    View your latest payslips and manage tax declarations.
                                </p>
                                <Link to="/payroll/investment-declaration"
                                    className="btn btn-sm btn-outline-primary rounded-pill">
                                    Go to Payroll <ArrowRight size={14} className="ms-1"/>
                                </Link>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm p-4 h-100 bg-white custom-card">
                                <div className="p-2 bg-success bg-opacity-10 rounded-3 mb-3 d-inline-block">
                                    <Calendar className="text-success" size={24}/>
                                </div>
                                <h6 className="fw-bold">Attendance Log</h6>
                                <p className="small text-muted">
                                    Review your work hours and holiday calendar.
                                </p>
                                <Link to="/attendance"
                                    className="btn btn-sm btn-outline-success rounded-pill">
                                    View Logs <ArrowRight size={14} className="ms-1"/>
                                </Link>
                            </Card>
                        </Col>
                    </Row>

                    <div className="mt-4">
                        <h6 className="fw-bold mb-3">Recent Attendance</h6>
                        <AttendanceHistory data={history} loading={loadingHistory}/>
                    </div>
                </Col>

                <Col lg={4}>
                    {/* ── Birthday/Anniversary Widget — ALL employees ── */}
                    <BirthdayAnniversaryWidget/>

                    {/* ── Upcoming Holidays — live from API ── */}
                    <UpcomingHolidaysWidget/>

                    {/* ── Announcements ── */}
                    <AnnouncementTimeline/>
                </Col>
            </Row>
        </div>
    );
};

export default EmployeeDashboard;
